/**
 * The QuickStock identity.
 *
 * Taken from quickstock.co.uk rather than invented here: Pacifico set lowercase
 * and italic, "quick" in cyan and "stock" in magenta. The previous mark was a
 * drawn barcode, which was a reasonable guess at a stock-control product but was
 * not the company's mark — someone arriving from the website found nothing they
 * recognised.
 *
 * The compact mark is still used where the logotype will not fit — the phone
 * header and the boot splash — but no longer sits beside it. Two marks side by
 * side is one more than an identity needs.
 *
 * ── Why the colours are not literal in light mode ──────────────────────────
 *
 * The website's cyan is #00ddff on a near-black ground, where it glows. On white
 * the same value is 1.4:1 and reads as a rendering fault. Rather than abandon
 * the colours or accept the washout, the mark carries light and dark variants —
 * standard practice for a neon identity — deepened just enough to hold on paper
 * while staying unmistakably cyan and magenta. Only the dark theme gets the
 * glow, because a glow on white looks like a mistake.
 *
 * Pacifico ships no italic face, so the slant is synthesised. That is exactly
 * what the website does, so the two match.
 */
import { cn } from '../lib/cn';

/**
 * The wordmark as type.
 *
 * `aria-label` on the wrapper with the halves hidden: read as one word, not as
 * "quick" then "stock".
 */
export function Logotype({
  size = 'sm',
  className,
}: {
  size?: 'sm' | 'lg';
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'qs-logotype select-none',
        size === 'lg' ? 'text-[1.75rem]' : 'text-[1.0625rem]',
        className,
      )}
      aria-label="QuickStock"
    >
      <span className="qs-logotype-quick" aria-hidden>
        quick
      </span>
      <span className="qs-logotype-stock" aria-hidden>
        stock
      </span>
    </span>
  );
}

/**
 * The compact mark, for anywhere the full wordmark will not fit — the phone
 * header and the boot splash.
 *
 * This is the same file the browser tab uses, not a second drawing of it. It
 * used to be an inline SVG of three stacked bars, invented here as "stock on a
 * shelf" and sharing nothing with the identity; the mark is now a lowercase q
 * bent as a neon tube, cyan bowl into magenta stem, which is the same neon the
 * wordmark is set in.
 *
 * The file is `brand-mark.svg` and not `icon.svg` on purpose. The old tile lived
 * at `/icon.svg`, and reusing that path for a different image would have left
 * every browser — and every installed PWA, whose service worker updates only
 * when the reader accepts the prompt — serving the old drawing from cache at
 * the new name's URL. A new path has no cached predecessor.
 *
 * The vector, not one of the bitmaps: this renders at 20-28px, where a fixed
 * PNG would be resampled by whatever pixel ratio the screen happens to have.
 * `aria-hidden` because every place it appears already names the product.
 */
export function BrandTile({ size = 18 }: { size?: number }): React.JSX.Element {
  return (
    <img
      src="/brand-mark.svg"
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      /* No CSS rounding: the file carries its own corners, and a second radius
         on top of them shaves the first. */
      className="shrink-0"
      style={{ width: size, height: size }}
    />
  );
}
